import React, { useMemo, useState } from "react";
import {
  Check,
  Inbox,
  ListTree,
  Loader2,
  Plus,
  ShieldPlus,
} from "lucide-react";
import { toast } from "sonner";

const getPermissionId = (permission) => {
  return String(
    permission?.pk_permission_id || permission?.permission_id || "",
  );
};

const getPermissionName = (permission) => {
  return (
    permission?.permission_name || permission?.permissionName || "Permission"
  );
};

const getRoleId = (role) => {
  return String(role?.pk_role_id || role?.role_id || role?.id || "");
};

const getRoleName = (role) => {
  const rawRole =
    role?.role ||
    role?.roleName ||
    role?.role_name ||
    "Unnamed Role";

  return String(rawRole).split("|")[0]?.trim() || "Unnamed Role";
};

const getAdminId = (admin) => {
  if (!admin || typeof admin !== "object") return "";

  const id =
    admin.pk_admin_id ??
    admin.admin_id ??
    admin.adminId ??
    admin.id ??
    admin.admin_loginid ??
    admin.userId;

  return id === null || id === undefined ? "" : String(id).trim();
};

const getAdminName = (admin) => {
  const firstName = admin?.first_name || admin?.firstName || "";
  const lastName = admin?.last_name || admin?.lastName || "";
  const fullName = `${firstName} ${lastName}`.trim();

  return admin?.admin_username || fullName || "Unnamed Admin";
};

const getAdminLoginId = (admin) => {
  return String(admin?.admin_loginid || admin?.userId || "");
};

const getRoleNameById = (roles = [], roleId = "") => {
  const role = roles.find((item) => String(getRoleId(item)) === String(roleId));
  return role ? getRoleName(role) : "No Role";
};

const getSidebarAccessId = (item) => {
  return String(item?.id || item?.pk_sidebar_id || "");
};

const getMenuId = (item) => {
  return String(item?.pk_menu_id || item?.menuId || "");
};

const getSubmenuId = (item) => {
  return String(item?.pk_submenu_id || item?.submenuId || item?.id || "");
};

const getMenuTitle = (item) => {
  return item?.menu_title || item?.menuTitle || "Untitled Menu";
};

const getSubmenuTitle = (item) => {
  return (
    item?.submenu_title || item?.submenuTitle || item?.menu_title || "Untitled"
  );
};

const getMenuStatus = (item) => {
  return (
    item?.status === "Y" || item?.is_active === "Y" || item?.is_active === 1
  );
};

const buildAccessKey = (item) => {
  return `${getMenuId(item)}_${getSubmenuId(item)}`;
};

const buildSidebarSelectionKey = (item) => {
  if (!item || typeof item !== "object") {
    return "";
  }

  const menuId = getMenuId(item);
  const submenuId = getSubmenuId(item);
  const itemId = String(item?.id || "");
  const url = String(item?.submenu_url || item?.menu_url || "");

  return [
    menuId || "menu",
    submenuId || itemId || "submenu",
    url || "no-url",
  ].join("_");
};

const buildAccessManagementKey = (item) => {
  return `${item?.permissionId}_${item?.menuId}_${item?.submenuId}`;
};

const groupPermissionsByModule = (permissions = []) => {
  return permissions.reduce((grouped, permission) => {
    const moduleName = permission?.module_name || "Other Permissions";

    if (!grouped[moduleName]) {
      grouped[moduleName] = [];
    }

    grouped[moduleName].push(permission);
    return grouped;
  }, {});
};

const buildRolePermissionRowMap = (rolePermissions = []) => {
  const safeRolePermissions = Array.isArray(rolePermissions)
    ? rolePermissions
    : [];

  return safeRolePermissions.reduce((map, item) => {
    if (!item || typeof item !== "object") return map;

    const permissionId =
      item.permission_id === null || item.permission_id === undefined
        ? ""
        : String(item.permission_id);

    if (!permissionId) return map;

    map[permissionId] = item;
    return map;
  }, {});
};

const buildActivePermissionIdSet = (rolePermissions = []) => {
  const safeRolePermissions = Array.isArray(rolePermissions)
    ? rolePermissions
    : [];

  return new Set(
    safeRolePermissions
      .filter(
        (item) =>
          item &&
          typeof item === "object" &&
          (item.is_active === "Y" ||
            item.is_active === 1 ||
            item.is_active === "1"),
      )
      .map((item) => item?.permission_id)
      .filter((id) => id !== null && id !== undefined && id !== "")
      .map(String),
  );
};

const flattenSidebarAccess = (accessList = []) => {
  const result = [];

  accessList.forEach((menu) => {
    const submenus = Array.isArray(menu?.submenus) ? menu.submenus : [];

    if (submenus.length === 0) {
      result.push({
        id: getSidebarAccessId(menu),
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: menu?.status,
        submenu_title: menu?.menu_title,
        submenu_icon: menu?.menu_icon,
        submenu_url: menu?.menu_url,
      });
      return;
    }

    submenus.forEach((submenu) => {
      result.push({
        id: submenu?.id || menu?.id,
        pk_menu_id: menu?.pk_menu_id,
        pk_submenu_id: submenu?.id || menu?.pk_submenu_id,
        menu_title: menu?.menu_title,
        menu_icon: menu?.menu_icon,
        menu_url: menu?.menu_url,
        menu_type: menu?.menu_type,
        status: submenu?.status || menu?.status,
        submenu_title: submenu?.submenu_title || menu?.menu_title,
        submenu_icon: submenu?.submenu_icon || menu?.menu_icon,
        submenu_url: submenu?.submenu_url || menu?.menu_url,
      });
    });
  });

  return result;
};

const buildUserAccessMap = (userAccessList = []) => {
  const flatUserAccess = flattenSidebarAccess(userAccessList);

  return flatUserAccess.reduce((map, item) => {
    const key = buildAccessKey(item);
    map[key] = item;
    return map;
  }, {});
};

const searchPermissions = (permissions = [], searchText = "") => {
  const safePermissions = Array.isArray(permissions) ? permissions : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safePermissions;

  return safePermissions.filter((permission) => {
    if (!permission || typeof permission !== "object") return false;

    return [
      permission?.permission_name,
      permission?.permissionName,
      permission?.module_name,
      permission?.pk_permission_id,
      permission?.permission_id,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const searchAdmins = (admins = [], searchText = "") => {
  const safeAdmins = Array.isArray(admins) ? admins : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safeAdmins;

  return safeAdmins.filter((admin) => {
    if (!admin || typeof admin !== "object") {
      return false;
    }

    return [
      getAdminName(admin),
      getAdminLoginId(admin),
      getAdminId(admin),
      admin?.first_name,
      admin?.last_name,
      admin?.admin_username,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const normalizeSearchValue = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).toLowerCase();
};

const searchSidebarAccess = (accessList = [], searchText = "") => {
  const safeList = Array.isArray(accessList) ? accessList : [];
  const query = normalizeSearchValue(searchText).trim();

  if (!query) return safeList;

  return safeList.filter((item) => {
    if (!item || typeof item !== "object") return false;

    return [
      item?.menu_title,
      item?.submenu_title,
      item?.menu_url,
      item?.submenu_url,
      item?.id,
      item?.pk_menu_id,
      item?.pk_submenu_id,
    ].some((value) => normalizeSearchValue(value).includes(query));
  });
};

const getModuleCount = (permissions = []) => {
  return Object.keys(groupPermissionsByModule(permissions)).length;
};

const InlineLoader = ({ text = "Loading...", className = "" }) => {
  return (
    <div
      className={`flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500 ${className}`}
    >
      <Loader2 className="h-4 w-4 animate-spin" />
      <span>{text}</span>
    </div>
  );
};

const EmptyState = ({
  title = "No data found",
  description = "Nothing to show here.",
}) => {
  return (
    <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
      <div className="mb-3 rounded-full bg-white p-3 shadow-sm">
        <Inbox className="h-6 w-6 text-slate-400" />
      </div>
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <p className="mt-1 max-w-sm text-xs text-slate-500">{description}</p>
    </div>
  );
};

const AccessManagementSelector = ({
  permissions = [],
  allSidebarAccess = [],
  selectedSidebarKeys = [],
  selectedPermissionIds = [],
  onSelectedSidebarKeysChange,
  onSelectedPermissionIdsChange,
}) => {
  const [sidebarSearchText, setSidebarSearchText] = useState("");
  const [permissionSearchText, setPermissionSearchText] = useState("");

  const flatSidebarAccess = useMemo(() => {
    return flattenSidebarAccess(
      Array.isArray(allSidebarAccess) ? allSidebarAccess : [],
    );
  }, [allSidebarAccess]);

  const filteredSidebarAccess = useMemo(() => {
    const searchedItems = searchSidebarAccess(
      flatSidebarAccess,
      sidebarSearchText,
    );

    const uniqueItemsMap = new Map();

    searchedItems.forEach((item) => {
      if (!item || typeof item !== "object") return;

      const key = buildSidebarSelectionKey(item);

      if (!key) return;

      if (!uniqueItemsMap.has(key)) {
        uniqueItemsMap.set(key, item);
      }
    });

    return Array.from(uniqueItemsMap.values());
  }, [flatSidebarAccess, sidebarSearchText]);

  const filteredPermissions = useMemo(() => {
    return searchPermissions(
      Array.isArray(permissions) ? permissions : [],
      permissionSearchText,
    );
  }, [permissions, permissionSearchText]);

  const groupedPermissions = useMemo(() => {
    return groupPermissionsByModule(filteredPermissions);
  }, [filteredPermissions]);

  const safeSelectedSidebarKeys = useMemo(() => {
    if (!Array.isArray(selectedSidebarKeys)) return [];

    return [
      ...new Set(
        selectedSidebarKeys
          .filter((key) => key !== null && key !== undefined && key !== "")
          .map((key) => String(key)),
      ),
    ];
  }, [selectedSidebarKeys]);

  const safeSelectedPermissionIds = useMemo(() => {
    if (!Array.isArray(selectedPermissionIds)) return [];

    return [
      ...new Set(
        selectedPermissionIds
          .filter((id) => id !== null && id !== undefined && id !== "")
          .map((id) => String(id)),
      ),
    ];
  }, [selectedPermissionIds]);

  const selectedSidebarKeySet = useMemo(() => {
    return new Set(safeSelectedSidebarKeys);
  }, [safeSelectedSidebarKeys]);

  const selectedPermissionSet = useMemo(() => {
    return new Set(safeSelectedPermissionIds);
  }, [safeSelectedPermissionIds]);

  const toggleSidebarItem = (itemKey) => {
    if (!itemKey) return;

    const currentKeys = Array.isArray(selectedSidebarKeys)
      ? selectedSidebarKeys.map(String)
      : [];

    const alreadySelected = currentKeys.includes(String(itemKey));

    const nextKeys = alreadySelected
      ? currentKeys.filter((key) => key !== String(itemKey))
      : [...currentKeys, String(itemKey)];

    console.log("SIDEBAR SELECTION UPDATE", {
      itemKey,
      alreadySelected,
      previousCount: currentKeys.length,
      nextCount: nextKeys.length,
      nextKeys,
    });

    onSelectedSidebarKeysChange(nextKeys);
  };

  const selectAllVisibleSidebar = () => {
    try {
      const nextSet = new Set(safeSelectedSidebarKeys);

      filteredSidebarAccess.forEach((item) => {
        const itemKey = buildSidebarSelectionKey(item);

        if (itemKey) {
          nextSet.add(String(itemKey));
        }
      });

      onSelectedSidebarKeysChange(Array.from(nextSet));
    } catch (error) {
      console.error("Select visible sidebar error:", error);
      toast.error("Unable to select sidebar options");
    }
  };

  const clearSidebarSelection = () => {
    onSelectedSidebarKeysChange([]);
  };

  const togglePermission = (permission, checked) => {
    const permissionId = getPermissionId(permission);

    console.group("CREATE ROLE PERMISSION CLICK");
    console.log("Permission:", permission);
    console.log("Permission ID:", permissionId);
    console.log("Checked:", checked);
    console.groupEnd();

    if (!permissionId) {
      console.error("PERMISSION ID EMPTY:", permission);
      return;
    }

    onSelectedPermissionIdsChange((previousValue) => {
      try {
        console.log("Previous permission selection:", previousValue);

        const previousIds = Array.isArray(previousValue)
          ? previousValue.filter(Boolean).map((id) => String(id))
          : [];

        const nextSet = new Set(previousIds);

        if (checked) {
          nextSet.add(String(permissionId));
        } else {
          nextSet.delete(String(permissionId));
        }

        const nextIds = Array.from(nextSet);

        console.log("Next permission IDs:", nextIds);
        console.log("Next permission count:", nextIds.length);

        return nextIds;
      } catch (error) {
        console.error("TOGGLE PERMISSION STATE ERROR:", error);
        console.error("Error stack:", error?.stack);

        return Array.isArray(previousValue) ? previousValue : [];
      }
    });
  };

  const selectAllVisiblePermissions = () => {
    try {
      const nextSet = new Set(safeSelectedPermissionIds);

      filteredPermissions.forEach((permission) => {
        const permissionId = getPermissionId(permission);

        if (permissionId) {
          nextSet.add(String(permissionId));
        }
      });

      onSelectedPermissionIdsChange(Array.from(nextSet));
    } catch (error) {
      console.error("Select visible permission error:", error);
      toast.error("Unable to select permissions");
    }
  };

  const clearPermissionSelection = () => {
    onSelectedPermissionIdsChange([]);
  };

  return (
    <div className="grid h-full min-h-0 min-w-0 grid-rows-[max-content_minmax(0,1fr)] gap-3 overflow-hidden">
      {/* Top summary */}
      <div className="min-w-0 shrink-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-orange-50 p-2.5 text-orange-600">
              <ListTree className="h-5 w-5" />
            </div>

            <div>
              <h3 className="text-base font-bold text-slate-900">
                Role Access Setup
              </h3>

              <p className="mt-1 text-sm text-slate-500">
                Select sidebar menus and permissions.
              </p>
            </div>
          </div>

          <div className="flex shrink-0 gap-2">
            <div className="rounded-xl bg-orange-50 px-4 py-2">
              <p className="text-xs font-semibold text-orange-700">Sidebar</p>
              <p className="text-xl font-black text-orange-700">
                {safeSelectedSidebarKeys.length}
              </p>
            </div>

            <div className="rounded-xl bg-slate-100 px-4 py-2">
              <p className="text-xs font-semibold text-slate-600">
                Permissions
              </p>
              <p className="text-xl font-black text-slate-800">
                {safeSelectedPermissionIds.length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Internal scrolling panels */}
      <div className="grid min-h-0 min-w-0 grid-cols-1 gap-3 overflow-hidden lg:grid-cols-2">
        {/* Left */}
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="shrink-0 border-b border-slate-100">
            {/* Existing left heading, buttons and search */}
          </div>

          {filteredSidebarAccess.length === 0 ? (
            <div className="min-h-0 flex-1 overflow-auto p-4">
              <EmptyState
                title="No sidebar options found"
                description="Load all sidebar access first."
              />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-3">
              <div className="space-y-2 pb-3">
                {filteredSidebarAccess.map((item) => {
                  const itemKey = buildSidebarSelectionKey(item);
                  const checked = selectedSidebarKeySet.has(String(itemKey));

                  return (
                    <button
                      key={itemKey}
                      type="button"
                      onClick={() => toggleSidebarItem(itemKey)}
                      aria-pressed={checked}
                      className={`flex w-full cursor-pointer items-start gap-3 rounded-xl border p-3 text-left transition ${
                        checked
                          ? "border-orange-200 bg-orange-50"
                          : "border-slate-200 bg-white hover:bg-slate-50"
                      }`}
                    >
                      <span
                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                          checked
                            ? "border-orange-500 bg-orange-500 text-white"
                            : "border-slate-300 bg-white text-transparent"
                        }`}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </span>

                      <span className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
                        <span className="shrink-0 text-sm font-bold text-slate-900">
                          {getMenuTitle(item)}
                        </span>

                        <span className="shrink-0 text-xs font-medium text-slate-600">
                          {getSubmenuTitle(item)}
                        </span>

                        <span className="min-w-0 flex-1 truncate text-xs text-slate-400">
                          {String(item?.submenu_url || item?.menu_url || "-")}
                        </span>

                        <span className="shrink-0 whitespace-nowrap text-xs text-slate-400">
                          Menu ID: {getMenuId(item) || "N/A"} | Submenu ID:{" "}
                          {getSubmenuId(item) || "N/A"}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right */}
        <div className="flex min-h-0 min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="shrink-0 border-b border-slate-100">
            {/* Existing right heading, buttons and search */}
          </div>

          {Object.keys(groupedPermissions).length === 0 ? (
            <div className="min-h-0 flex-1 overflow-auto p-4">
              <EmptyState
                title="No permissions found"
                description="Try changing the search text."
              />
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-3">
              <div className="grid grid-cols-1 gap-3 pb-3">
                {Object.entries(groupedPermissions).map(
                  ([moduleName, items]) => (
                    <div
                      key={moduleName}
                      className="w-full overflow-hidden rounded-xl border border-slate-200"
                    >
                      <div className="border-b border-slate-100 bg-slate-50 px-3 py-2">
                        <p className="text-xs font-bold uppercase tracking-wide text-slate-500">
                          {moduleName}
                        </p>
                      </div>

                      <div className="space-y-2 p-2">
                        {items.map((permission) => {
                          const permissionId = getPermissionId(permission);
                          const checked =
                            selectedPermissionSet.has(permissionId);

                          return (
                            <button
                              key={permissionId}
                              type="button"
                              onClick={() =>
                                togglePermission(permission, !checked)
                              }
                              aria-pressed={checked}
                              className={`flex w-full cursor-pointer items-start gap-3 rounded-xl border p-3 text-left transition ${
                                checked
                                  ? "border-orange-200 bg-orange-50"
                                  : "border-slate-200 bg-white hover:bg-slate-50"
                              }`}
                            >
                              <span
                                className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                  checked
                                    ? "border-orange-500 bg-orange-500 text-white"
                                    : "border-slate-300 bg-white text-transparent"
                                }`}
                              >
                                <Check className="h-3.5 w-3.5" />
                              </span>

                              <span className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden">
                                <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                                  {getPermissionName(permission)}
                                </span>

                                <span className="shrink-0 whitespace-nowrap text-xs text-slate-400">
                                  Permission ID: {permissionId}
                                </span>
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const CreateRoleForm = ({
  permissions = [],
  allSidebarAccess = [],
  permissionsLoading = false,
  userAccessLoading = false,
  createRoleLoading = false,
  onCreateRole,
}) => {
  const [roleName, setRoleName] = useState("");
  const [selectedSidebarKeys, setSelectedSidebarKeys] = useState([]);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const flatSidebarItems = flattenSidebarAccess(
      Array.isArray(allSidebarAccess) ? allSidebarAccess : [],
    );

    const selectedKeySet = new Set(
      Array.isArray(selectedSidebarKeys) ? selectedSidebarKeys.map(String) : [],
    );

    const accessManagement = flatSidebarItems
      .filter((item) => selectedKeySet.has(buildSidebarSelectionKey(item)))
      .map((item) => ({
        menuId: getMenuId(item),
        submenuId: getSubmenuId(item),
      }))
      .filter((item) => item.menuId && item.submenuId);

    const success = await onCreateRole?.({
      roleName: roleName.trim(),
      accessManagement,
      permissionIds: Array.isArray(selectedPermissionIds)
        ? selectedPermissionIds.map(String)
        : [],
    });

    if (success) {
      setRoleName("");
      setSelectedSidebarKeys([]);
      setSelectedPermissionIds([]);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid min-h-0 min-w-0 flex-1 grid-rows-[auto_minmax(0,1fr)] gap-3 overflow-hidden"
    >
      {/* Fixed role information section */}
      <div className="shrink-0 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-orange-50 p-2.5 text-orange-600">
              <ShieldPlus className="h-5 w-5" />
            </div>

            <div>
              <h2 className="text-base font-bold text-slate-900">
                Create New Role
              </h2>

              <p className="mt-1 text-sm text-slate-500">
                Enter role name, select sidebar visibility and select permission
                controls for this role.
              </p>
            </div>
          </div>

          <button
            type="submit"
            disabled={createRoleLoading}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-3 text-sm font-bold text-white shadow-sm transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            {createRoleLoading ? "Creating..." : "Create Role"}
          </button>
        </div>

        <div className="mt-3">
          <label className="mb-1.5 block text-xs font-bold text-slate-600">
            Role Name
          </label>

          <input
            type="text"
            value={roleName}
            onChange={(event) => setRoleName(event.target.value)}
            placeholder="Example: City Admin, Operations Manager"
            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-xl bg-orange-50 p-3">
            <p className="text-xs font-bold text-orange-700">Role Name</p>
            <p className="mt-1 truncate text-sm font-bold text-orange-900">
              {roleName || "Not entered"}
            </p>
          </div>

          <div className="rounded-xl bg-slate-100 p-3">
            <p className="text-xs font-bold text-slate-600">Sidebar Options</p>

            <p className="mt-1 text-xl font-black text-slate-900">
              {Array.isArray(selectedSidebarKeys)
                ? selectedSidebarKeys.length
                : 0}
            </p>
          </div>

          <div className="rounded-xl bg-slate-100 p-3">
            <p className="text-xs font-bold text-slate-600">Permissions</p>

            <p className="mt-1 text-xl font-black text-slate-900">
              {Array.isArray(selectedPermissionIds)
                ? selectedPermissionIds.length
                : 0}
            </p>
          </div>
        </div>
      </div>

      {/* Remaining height */}
      <div className="min-h-0 min-w-0 overflow-hidden">
        {permissionsLoading || userAccessLoading ? (
          <div className="h-full min-h-0 overflow-auto">
            <InlineLoader text="Loading role creation data..." />
          </div>
        ) : (
          <AccessManagementSelector
            permissions={permissions}
            allSidebarAccess={allSidebarAccess}
            selectedSidebarKeys={selectedSidebarKeys}
            selectedPermissionIds={selectedPermissionIds}
            onSelectedSidebarKeysChange={setSelectedSidebarKeys}
            onSelectedPermissionIdsChange={setSelectedPermissionIds}
          />
        )}
      </div>
    </form>
  );
};

export default function CreateRoleTab(props) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <CreateRoleForm {...props} />
    </div>
  );
}
